var { initIframePrint } = require('./shared/iframe-print');

(function() {
    /**
     * Checklist Builder - Main Application
     * Manages checklist categories, tasks, and data persistence
     */

    // Configuration
    const STORAGE_KEY = 'checklist-builder-data';
    const STORAGE_VERSION = 'v2'; // Increment this to reset user data

    // State
    let categories = [];
    let editingCategoryId = null;

    // DOM Elements (initialized after DOM is ready)
    let titleInput;
    let dateInput;
    let notesInput;
    let newCategoryInput;
    let container;
    let emptyState;
    let progressBar;
    let progressText;
    let editCategoryOverlay;
    let editCategoryName;

    // Data from server
    let placeholderData = {};
    let sampleData = {};

    /**
     * Utility: Generate unique ID
     * @returns {string} Unique identifier
     */
    function generateId() {
        return 'id-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
    }

    /**
     * Utility: Get today's date string in YYYY-MM-DD format (local timezone)
     * @returns {string} Today's date in local timezone
     */
    function getTodayDateString() {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    /**
     * Utility: Escape HTML special characters
     * @param {string} text - Text to escape
     * @returns {string} Escaped HTML
     */
    function escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    /**
     * Perform the actual clear action
     */
    function performClear() {
        categories = [];
        titleInput.value = '';
        notesInput.value = '';
        dateInput.value = getTodayDateString();
        renderCategories();
        saveToLocalStorage();
    }

    /**
     * Initialize clear confirmation modal controls
     */
    function initClearConfirmation() {
        const clearButton = document.getElementById('btn-clear-all');
        const overlay = document.getElementById('clearConfirmationOverlay');
        
        console.log('Initializing clear confirmation', { 
            hasClearButton: !!clearButton, 
            hasOverlay: !!overlay 
        });
        
        if (!clearButton || !overlay) {
            return;
        }

        const closeButtons = overlay.querySelectorAll('[data-clear-confirmation="close"]');
        const confirmButton = document.getElementById('clearConfirmationConfirm');

        clearButton.addEventListener('click', function () {
            console.log('Clear button clicked');
            if (categories.length === 0 && !titleInput.value && !notesInput.value) {
                console.log('Nothing to clear, ignoring');
                return;
            }
            openClearConfirmation(overlay);
        });

        closeButtons.forEach(function (button) {
            button.addEventListener('click', function () {
                closeClearConfirmation(overlay);
            });
        });

        // Close on overlay background click
        overlay.addEventListener('click', function (e) {
            if (e.target === overlay) {
                closeClearConfirmation(overlay);
            }
        });

        // Close on Escape key
        document.addEventListener('keydown', function (e) {
            if (e.key === 'Escape' && overlay.classList.contains('is-visible')) {
                closeClearConfirmation(overlay);
            }
        });

        if (confirmButton) {
            confirmButton.addEventListener('click', function () {
                console.log('Clear confirmed');
                performClear();
                closeClearConfirmation(overlay);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            });
        }
    }

    /**
     * Open the clear confirmation modal
     * @param {HTMLElement} overlay The modal overlay element
     */
    function openClearConfirmation(overlay) {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        if (scrollbarWidth > 0) {
            document.body.style.paddingRight = scrollbarWidth + 'px';
        }
        overlay.classList.add('is-visible');
        overlay.setAttribute('aria-hidden', 'false');
        document.body.classList.add('clear-confirmation-open');
    }

    /**
     * Close the clear confirmation modal
     * @param {HTMLElement} overlay The modal overlay element
     */
    function closeClearConfirmation(overlay) {
        overlay.classList.remove('is-visible');
        overlay.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('clear-confirmation-open');
        document.body.style.paddingRight = '';
    }

    /**
     * Save checklist data to local storage
     */
    function saveToLocalStorage() {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
            version: STORAGE_VERSION,
            title: titleInput.value,
            date: dateInput.value,
            notes: notesInput.value,
            categories: categories
        }));
    }

    /**
     * Load checklist data from local storage
     * @returns {boolean} True if data was loaded successfully
     */
    function loadFromLocalStorage() {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            try {
                const data = JSON.parse(saved);
                // Check version - if old version or no version, clear and return false
                if (data.version !== STORAGE_VERSION) {
                    localStorage.removeItem(STORAGE_KEY);
                    return false;
                }
                titleInput.value = data.title || '';
                dateInput.value = data.date || getTodayDateString();
                notesInput.value = data.notes || '';
                categories = data.categories || [];
                return true;
            } catch (e) {
                console.error('Error loading saved data:', e);
                localStorage.removeItem(STORAGE_KEY);
            }
        }
        return false;
    }

    /**
     * Load data from a template
     * @param {object} template - Template data with categories and tasks
     */
    function loadDataFromTemplate(template) {
        titleInput.value = template.title || '';
        notesInput.value = template.notes || '';
        dateInput.value = template.date || getTodayDateString();
        categories = (template.categories || []).map(cat => ({
            id: generateId(),
            name: cat.name,
            tasks: (cat.tasks || []).map(text => ({ text, checked: false }))
        }));
        renderCategories();
    }

    /**
     * Update progress bar and text
     */
    function updateProgress() {
        let total = 0, completed = 0;
        categories.forEach(cat => cat.tasks.forEach(task => {
            total++;
            if (task.checked) completed++;
        }));

        const pct = total > 0 ? (completed / total * 100) : 0;
        if (progressBar) {
            progressBar.style.width = pct + '%';
            progressBar.className = 'progress-bar ' + (pct === 100 && total > 0 ? 'bg-success' : pct >= 50 ? 'bg-primary' : 'bg-warning');
        }
        if (progressText) {
            progressText.textContent = `${completed} of ${total} completed`;
        }
    }

    /**
     * Render all categories
     */
    function renderCategories() {
        if (!container) return;
        
        container.querySelectorAll('.category-card').forEach(el => el.remove());

        if (categories.length === 0) {
            if (emptyState) {
                emptyState.classList.remove('d-none');
            }
            updateProgress();
            saveToLocalStorage();
            return;
        }

        if (emptyState) {
            emptyState.classList.add('d-none');
        }
        categories.forEach(cat => container.appendChild(createCategoryElement(cat)));
        updateProgress();
        saveToLocalStorage();
    }

    /**
     * Create category DOM element
     * @param {object} category - Category object
     * @returns {HTMLElement} Category card element
     */
    function createCategoryElement(category) {
        const div = document.createElement('div');
        div.className = 'category-card';
        div.dataset.id = category.id;

        const completed = category.tasks.filter(t => t.checked).length;
        const total = category.tasks.length;

        div.innerHTML = `
            <div class="category-header">
                <div class="category-title-section">
                    <i class="bi bi-folder2-open me-2" aria-hidden="true"></i>
                    <span class="category-name">${escapeHtml(category.name)}</span>
                    <span class="category-count">${completed}/${total}</span>
                </div>
                <div class="category-actions">
                    <button type="button" class="btn-category-action btn-edit-cat" title="Edit category">
                        <i class="bi bi-pencil"></i> 
                    </button>
                </div>
            </div>
            <div class="category-tasks">
                ${category.tasks.map((task, i) => `
                    <div class="task-item ${task.checked ? 'checked' : ''}" data-index="${i}">
                        <label class="task-checkbox">
                            <input type="checkbox" ${task.checked ? 'checked' : ''} />
                            <span class="checkmark"></span>
                        </label>
                        <span class="task-text">${escapeHtml(task.text)}</span>
                        <button type="button" class="btn-delete-task" title="Delete task">
                            <i class="bi bi-x"></i>
                        </button>
                    </div>
                `).join('')}
            </div>
            <div class="add-task-section">
                <div class="input-group input-group-sm">
                    <input type="text" class="form-control add-task-input" placeholder="Add a task..." maxlength="100" />
                    <button type="button" class="btn btn-outline-primary btn-add-task">
                        <i class="bi bi-plus-lg"></i>
                    </button>
                </div>
            </div>
        `;

        // Edit category button
        div.querySelector('.btn-edit-cat').addEventListener('click', () => openEditModal(category.id));

        // Task checkboxes
        div.querySelectorAll('.task-checkbox input').forEach((cb, i) => {
            cb.addEventListener('change', () => {
                category.tasks[i].checked = cb.checked;
                cb.closest('.task-item').classList.toggle('checked', cb.checked);
                div.querySelector('.category-count').textContent = `${category.tasks.filter(t => t.checked).length}/${category.tasks.length}`;
                updateProgress();
                saveToLocalStorage();
            });
        });

        // Delete task buttons
        div.querySelectorAll('.btn-delete-task').forEach((btn, i) => {
            btn.addEventListener('click', () => {
                category.tasks.splice(i, 1);
                renderCategories();
            });
        });

        // Add task functionality
        const taskInput = div.querySelector('.add-task-input');
        const addTask = () => {
            const text = taskInput.value.trim();
            if (text) {
                category.tasks.push({ text, checked: false });
                taskInput.value = '';
                renderCategories();
            }
        };

        div.querySelector('.btn-add-task').addEventListener('click', addTask);
        taskInput.addEventListener('keypress', e => e.key === 'Enter' && addTask());

        return div;
    }

    /**
     * Add a new category
     * @param {string} name - Category name
     * @returns {string|null} The ID of the new category, or null if not added
     */
    function addCategory(name) {
        const trimmed = name.trim();
        if (!trimmed) return null;
        const newId = generateId();
        categories.push({ id: newId, name: trimmed, tasks: [] });
        renderCategories();
        
        // Scroll to the newly added category and focus its task input
        scrollToCategoryAndFocus(newId);
        
        return newId;
    }

    /**
     * Scroll to a category card and focus its task input
     * @param {string} categoryId - The category ID to scroll to
     */
    function scrollToCategoryAndFocus(categoryId) {
        // Use setTimeout to ensure DOM is updated before scrolling
        setTimeout(function() {
            const categoryCard = container.querySelector('[data-id="' + categoryId + '"]');
            if (categoryCard) {
                // Scroll the category into view with some padding at the top
                categoryCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
                
                // Focus the task input after scrolling completes
                setTimeout(function() {
                    const taskInput = categoryCard.querySelector('.add-task-input');
                    if (taskInput) {
                        taskInput.focus();
                    }
                }, 300);
            }
        }, 50);
    }




    /**
     * Open edit category overlay
     * @param {string} id - Category ID
     */
    function openEditModal(id) {
        const cat = categories.find(c => c.id === id);
        if (!cat) return;
        editingCategoryId = id;
        if (editCategoryName) {
            editCategoryName.value = cat.name;
        }
        if (editCategoryOverlay) {
            openEditOverlay(editCategoryOverlay);
        }
    }

    /**
     * Open the edit category overlay
     * @param {HTMLElement} overlay The overlay element
     */
    function openEditOverlay(overlay) {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        if (scrollbarWidth > 0) {
            document.body.style.paddingRight = scrollbarWidth + 'px';
        }
        overlay.classList.add('is-visible');
        overlay.setAttribute('aria-hidden', 'false');
        document.body.classList.add('edit-category-open');
        
        // Focus the input after a short delay
        setTimeout(function() {
            if (editCategoryName) {
                editCategoryName.focus();
                editCategoryName.select();
            }
        }, 100);
    }

    /**
     * Close the edit category overlay
     * @param {HTMLElement} overlay The overlay element
     */
    function closeEditOverlay(overlay) {
        overlay.classList.remove('is-visible');
        overlay.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('edit-category-open');
        document.body.style.paddingRight = '';
        editingCategoryId = null;
    }

    /**
     * Update category name
     * @param {string} id - Category ID
     * @param {string} newName - New category name
     * @returns {boolean} True if category was updated
     */
    function updateCategory(id, newName) {
        const cat = categories.find(c => c.id === id);
        const trimmed = newName.trim();
        if (!cat || !trimmed) return false;
        cat.name = trimmed;
        renderCategories();
        return true;
    }

    /**
     * Delete a category
     * @param {string} id - Category ID
     */
    function deleteCategory(id) {
        categories = categories.filter(c => c.id !== id);
        renderCategories();
    }

    /**
     * Initialize the application
     */
    function init() {
        // Initialize DOM elements
        titleInput = document.getElementById('checklist-title');
        dateInput = document.getElementById('checklist-date');
        notesInput = document.getElementById('checklist-notes');
        newCategoryInput = document.getElementById('new-category-input');
        container = document.getElementById('categories-container');
        emptyState = document.getElementById('empty-state');
        progressBar = document.getElementById('progress-bar');
        progressText = document.getElementById('progress-text');
        editCategoryName = document.getElementById('edit-category-name');
        editCategoryOverlay = document.getElementById('editCategoryOverlay');

        // Clear old localStorage data to prevent stale dates
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            try {
                const data = JSON.parse(saved);
                if (data.version !== STORAGE_VERSION) {
                    localStorage.removeItem(STORAGE_KEY);
                }
            } catch (e) {
                localStorage.removeItem(STORAGE_KEY);
            }
        }

        // Set default date if empty
        if (dateInput && !dateInput.value) {
            dateInput.value = getTodayDateString();
        }
        
        // Add category input
        const addCategoryBtn = document.getElementById('btn-add-category');
        if (addCategoryBtn) {
            addCategoryBtn.addEventListener('click', function() {
                if (addCategory(newCategoryInput.value)) {
                    newCategoryInput.value = '';
                }
            });
        }

        if (newCategoryInput) {
            newCategoryInput.addEventListener('keypress', function(e) {
                if (e.key === 'Enter' && addCategory(newCategoryInput.value)) {
                    newCategoryInput.value = '';
                }
            });
        }

        // Load sample data
        const loadSampleBtn = document.getElementById('btn-load-sample');
        if (loadSampleBtn) {
            loadSampleBtn.addEventListener('click', function() {
                if (sampleData && sampleData.categories) {
                    loadDataFromTemplate(sampleData);
                    saveToLocalStorage();
                }
            });
        }

        // Initialize edit category overlay
        initEditCategoryOverlay();

        // Clear all - initialize confirmation dialog
        initClearConfirmation();

        // Auto-save on input
        if (titleInput) {
            titleInput.addEventListener('input', saveToLocalStorage);
        }
        if (notesInput) {
            notesInput.addEventListener('input', saveToLocalStorage);
        }
        if (dateInput) {
            dateInput.addEventListener('change', saveToLocalStorage);
        }

        // Load saved data from localStorage (if any)
        // If no saved data exists, start with empty checklist (don't load placeholder)
        loadFromLocalStorage();

        renderCategories();
    }

    /**
     * Initialize edit category overlay controls
     */
    function initEditCategoryOverlay() {
        if (!editCategoryOverlay) {
            return;
        }

        const closeButtons = editCategoryOverlay.querySelectorAll('[data-edit-category="close"]');
        const saveButton = document.getElementById('btn-save-category');
        const deleteButton = document.getElementById('btn-delete-category');

        // Close buttons
        closeButtons.forEach(function(button) {
            button.addEventListener('click', function() {
                closeEditOverlay(editCategoryOverlay);
            });
        });

        // Close on overlay background click
        editCategoryOverlay.addEventListener('click', function(e) {
            if (e.target === editCategoryOverlay) {
                closeEditOverlay(editCategoryOverlay);
            }
        });

        // Close on Escape key
        document.addEventListener('keydown', function(e) {
            if (e.key === 'Escape' && editCategoryOverlay.classList.contains('is-visible')) {
                closeEditOverlay(editCategoryOverlay);
            }
        });

        // Save button
        if (saveButton) {
            saveButton.addEventListener('click', function() {
                if (editingCategoryId && editCategoryName) {
                    const newName = editCategoryName.value.trim();
                    if (newName && updateCategory(editingCategoryId, newName)) {
                        closeEditOverlay(editCategoryOverlay);
                    }
                }
            });
        }

        // Save on Enter key in input
        if (editCategoryName) {
            editCategoryName.addEventListener('keypress', function(e) {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    if (editingCategoryId) {
                        const newName = editCategoryName.value.trim();
                        if (newName && updateCategory(editingCategoryId, newName)) {
                            closeEditOverlay(editCategoryOverlay);
                        }
                    }
                }
            });
        }

        // Delete button
        if (deleteButton) {
            deleteButton.addEventListener('click', function() {
                if (editingCategoryId) {
                    deleteCategory(editingCategoryId);
                    closeEditOverlay(editCategoryOverlay);
                }
            });
        }
    }

    // Initialize when DOM is ready
    function startApp() {
        console.log('ChecklistBuilder: Starting app...');

        // Wire iframe print flow
        initIframePrint({
            formId: 'cbPrintForm',
            iframeId: 'cbPrintFrame',
            buttonId: 'cbPrintBtn',
            readyLabel: '<i class="bi bi-printer me-2" aria-hidden="true"></i>Print Checklist',
        });

        // Get data from hidden element data attributes
        const dataElement = document.getElementById('checklist-builder-data');
        if (dataElement) {
            try {
                placeholderData = JSON.parse(dataElement.dataset.placeholder || '{}');
                sampleData = JSON.parse(dataElement.dataset.sample || '{}');
                console.log('ChecklistBuilder: Data loaded', { 
                    hasPlaceholder: !!placeholderData.categories,
                    hasSample: !!sampleData.categories 
                });
            } catch (e) {
                console.error('Error parsing checklist data:', e);
                placeholderData = {};
                sampleData = {};
            }
        } else {
            console.warn('ChecklistBuilder: Data element not found');
        }
        
        init();
        console.log('ChecklistBuilder: Initialization complete');
    }

    // Check if DOM is already loaded (script may be loaded after DOMContentLoaded)
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', startApp);
    } else {
        // DOM already loaded, initialize immediately
        startApp();
    }

    // Expose public API
    window.ChecklistBuilder = {
        addCategory,
        deleteCategory,
        updateCategory,
        renderCategories
    };
})();
